import React, { useState } from "react";
import {
  Alert,
  AlertTitle,
  Box,
  Button,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  List,
  ListItemButton,
  ListItemText,
} from "@mui/material";
import { useTranslation } from "react-i18next";
import useAuthenticatedFetch from "../../auth/authenticated";
import { Action } from "../../models/Action";

interface QaBannerProps {
  claimId: string;
  openQaFindingIds: string[];
  actions: Action[];
  onResolved: (released: boolean) => void;
}

type Mode = "finish-keep" | "finish-put-back" | "dismiss";

const QaBanner: React.FC<QaBannerProps> = ({
  claimId,
  openQaFindingIds,
  actions,
  onResolved,
}) => {
  const { t } = useTranslation();
  const getAccessHeader = useAuthenticatedFetch();
  const [pickerMode, setPickerMode] = useState<Mode | null>(null);
  const [pending, setPending] = useState(false);
  const [error, setError] = useState<string | null>(null);

  if (!openQaFindingIds || openQaFindingIds.length === 0) {
    return null;
  }

  const openFindings = openQaFindingIds.map((id) => {
    const action = actions.find((a) => a.id === id);
    return {
      id,
      title:
        action?.qa_payload?.subject ||
        action?.description ||
        t("DASHBOARD.QA_BANNER.UNKNOWN_FINDING"),
    };
  });

  const postQa = async (mode: Mode, qaActionId: string | null) => {
    const isDismiss = mode === "dismiss";
    const path = isDismiss ? "dismiss" : "finish";
    const body: { qa_action_id: string | null; put_back?: boolean } = {
      qa_action_id: qaActionId,
    };
    if (!isDismiss) {
      body.put_back = mode === "finish-put-back";
    }

    const response = await fetch(`/api/claims/${claimId}/qa/${path}`, {
      method: "POST",
      headers: await getAccessHeader(),
      body: JSON.stringify(body),
    });

    let data: { error?: string; detail?: string } = {};
    try {
      data = await response.json();
    } catch {
      // 204 / non-JSON body
    }

    return { ok: response.ok, data };
  };

  const callApi = async (mode: Mode, qaActionId: string | null) => {
    setPending(true);
    setError(null);
    const result = await postQa(mode, qaActionId);
    setPending(false);
    if (!result.ok) {
      setError(
        result.data.error ||
          result.data.detail ||
          t("DASHBOARD.QA_BANNER.ERROR_GENERIC"),
      );
      return;
    }
    setPickerMode(null);
    const released = mode === "finish-put-back" || mode === "dismiss";
    onResolved(released);
  };

  const handleClick = (mode: Mode) => {
    if (openFindings.length === 1) {
      callApi(mode, null);
    } else {
      setPickerMode(mode);
    }
  };

  const pickerTitle =
    pickerMode === "finish-keep"
      ? t("DASHBOARD.QA_BANNER.PICKER_KEEP_TITLE")
      : pickerMode === "finish-put-back"
        ? t("DASHBOARD.QA_BANNER.PICKER_PUT_BACK_TITLE")
        : t("DASHBOARD.QA_BANNER.PICKER_DISMISS_TITLE");

  return (
    <>
      <Alert
        severity="warning"
        sx={{ mb: "24px" }}
        action={
          <Box sx={{ display: "flex", gap: "8px", alignItems: "center", flexWrap: "wrap" }}>
            {pending && <CircularProgress size={18} />}
            <Button
              size="small"
              variant="outlined"
              color="inherit"
              disabled={pending}
              onClick={() => handleClick("finish-keep")}
            >
              {t("DASHBOARD.QA_BANNER.RESOLVE_KEEP")}
            </Button>
            <Button
              size="small"
              variant="outlined"
              color="inherit"
              disabled={pending}
              onClick={() => handleClick("finish-put-back")}
            >
              {t("DASHBOARD.QA_BANNER.RESOLVE_PUT_BACK")}
            </Button>
            <Button
              size="small"
              variant="outlined"
              color="inherit"
              disabled={pending}
              onClick={() => handleClick("dismiss")}
            >
              {t("DASHBOARD.QA_BANNER.DISMISS")}
            </Button>
          </Box>
        }
      >
        <AlertTitle>
          {t("DASHBOARD.QA_BANNER.TITLE", { count: openFindings.length })}
        </AlertTitle>
        <Box component="ul" sx={{ pl: "20px", m: 0 }}>
          {openFindings.map((f) => (
            <li key={f.id}>{f.title}</li>
          ))}
        </Box>
        {error && (
          <Box sx={{ mt: "8px", color: "error.main" }}>{error}</Box>
        )}
      </Alert>

      <Dialog
        open={pickerMode !== null}
        onClose={() => (!pending ? setPickerMode(null) : undefined)}
        fullWidth
        maxWidth="sm"
      >
        <DialogTitle>{pickerTitle}</DialogTitle>
        <DialogContent dividers>
          <List>
            {openFindings.map((f) => (
              <ListItemButton
                key={f.id}
                disabled={pending}
                onClick={() => pickerMode && callApi(pickerMode, f.id)}
              >
                <ListItemText primary={f.title} secondary={f.id} />
              </ListItemButton>
            ))}
          </List>
          {error && (
            <Box sx={{ mt: "8px", color: "error.main" }}>{error}</Box>
          )}
        </DialogContent>
        <DialogActions>
          <Button
            onClick={() => setPickerMode(null)}
            disabled={pending}
          >
            {t("GENERAL.CANCEL")}
          </Button>
        </DialogActions>
      </Dialog>
    </>
  );
};

export default QaBanner;
